import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useAudit } from '../hooks/useAudit';
import { differenceInYears, format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { isSF36, calcularDominiosSF36 } from '../utils/sf36Score';
import AvaliacaoPreOpTab from '../components/AvaliacaoPreOpTab';

const COMORBIDADES_LABELS = {
  has: 'HAS', diabetes: 'Diabetes', tabagismo: 'Tabagismo',
  obesidade: 'Obesidade', osteoporose: 'Osteoporose',
  doenca_renal: 'Doença renal', cardiopatia: 'Cardiopatia'
};

const RADIO_LABELS = {
  cobb_proximal: 'Cobb proximal', cobb_principal: 'Cobb principal',
  cobb_lombar: 'Cobb lombar', sva: 'SVA', csva: 'cSVA',
  lordose_cervical: 'Lordose cervical', lordose_lombar: 'Lordose lombar',
  cifose_t5_t12: 'Cifose T5-T12', deformidade_segmentar: 'Deformidade segmentar',
  slope_sacral: 'Slope sacral', pelvic_tilt: 'Pelvic tilt',
  incidencia_pelvica: 'Incidência pélvica', obliquidade_pelvica: 'Obliquidade pélvica'
};

// ─── Busca de paciente ────────────────────────────────────────────────────
function BuscaPaciente({ onSelect }) {
  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setResultados([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      const { data } = await supabase.from('pacientes').select('*')
        .or(`prontuario.ilike.%${query}%,iniciais.ilike.%${query}%`)
        .limit(8);
      setResultados(data || []);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{ position: 'relative' }}>
        <input
          className="form-input"
          style={{ fontSize: 16, padding: '12px 16px', paddingLeft: 40 }}
          placeholder="Buscar por prontuário ou iniciais..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, opacity: .5 }}>🔍</span>
      </div>
      {resultados.length > 0 && (
        <div className="card" style={{ marginTop: 8 }}>
          {resultados.map(p => (
            <div key={p.id} onClick={() => onSelect(p)}
              style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <div style={{ width: 36, height: 36, background: 'var(--blue)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, fontSize: 13, flexShrink: 0 }}>
                {p.iniciais.slice(0, 2)}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{p.iniciais}</div>
                <div className="text-sm text-muted">Prontuário {p.prontuario} · {differenceInYears(new Date(), new Date(p.data_nascimento))} anos · {p.sexo === 'M' ? 'Masculino' : p.sexo === 'F' ? 'Feminino' : 'Outro'}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {query.length >= 2 && resultados.length === 0 && !loading && (
        <div className="text-muted text-sm" style={{ marginTop: 12, textAlign: 'center' }}>Nenhum paciente encontrado.</div>
      )}
    </div>
  );
}

// ─── Modal: Incluir em pesquisa ───────────────────────────────────────────
function ModalIncluirPesquisa({ paciente, pesquisasJaIncluidas, onClose, onSaved }) {
  const { user } = useAuth();
  const [pesquisas, setPesquisas] = useState([]);
  const [form, setForm] = useState({ pesquisa_id: '', data_inclusao: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('pesquisas').select('id, titulo').eq('ativa', true)
      .then(({ data }) => {
        const ids = pesquisasJaIncluidas.map(p => p.pesquisas?.id);
        setPesquisas((data || []).filter(p => !ids.includes(p.id)));
      });
  }, [pesquisasJaIncluidas]);

  async function handleSave() {
    if (!form.pesquisa_id) { setError('Selecione uma pesquisa.'); return; }
    setSaving(true);
    const { error } = await supabase.from('pesquisa_pacientes').insert({
      pesquisa_id: form.pesquisa_id,
      paciente_id: paciente.id,
      data_inclusao: form.data_inclusao,
      incluido_por: user.id
    });
    setSaving(false);
    if (error) { setError(error.message); return; }
    onSaved();
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Incluir em pesquisa</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">⚠ {error}</div>}
          {pesquisas.length === 0 ? (
            <div className="alert alert-info">Nenhuma pesquisa ativa disponível para inclusão.</div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Pesquisa *</label>
                <select className="form-select" value={form.pesquisa_id} onChange={e => setForm(f => ({...f, pesquisa_id: e.target.value}))}>
                  <option value="">Selecione...</option>
                  {pesquisas.map(p => <option key={p.id} value={p.id}>{p.titulo}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Data de inclusão</label>
                <input className="form-input" type="date" value={form.data_inclusao} onChange={e => setForm(f => ({...f, data_inclusao: e.target.value}))} />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          {pesquisas.length > 0 && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Incluindo...' : 'Incluir paciente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Aba: Geral ───────────────────────────────────────────────────────────
function AbaGeral({ paciente, pesquisasPaciente, isPesquisador, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  return (
    <div>
      <div className="grid-2" style={{ gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-body">
            <div className="form-label">Iniciais</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--navy)' }}>{paciente.iniciais}</div>
            <div className="form-label" style={{ marginTop: 12 }}>Prontuário</div>
            <div className="mono" style={{ fontSize: 16 }}>{paciente.prontuario}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="form-label">Cadastrado em</div>
            <div className="text-sm">{format(new Date(paciente.criado_em), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}</div>
            <div className="form-label" style={{ marginTop: 12 }}>Pesquisas vinculadas</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>{pesquisasPaciente.length}</div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <h2>Pesquisas</h2>
          {isPesquisador && (
            <button className="btn btn-sm btn-primary" onClick={() => setShowModal(true)}>+ Incluir em pesquisa</button>
          )}
        </div>
        <div className="table-wrap">
          {pesquisasPaciente.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🔬</div><h3>Nenhuma pesquisa vinculada</h3></div>
          ) : (
            <table>
              <thead><tr><th>Pesquisa</th><th>Inclusão</th><th>Status</th></tr></thead>
              <tbody>
                {pesquisasPaciente.map(pp => (
                  <tr key={pp.id}>
                    <td><strong>{pp.pesquisas?.titulo}</strong></td>
                    <td className="text-sm">{pp.data_inclusao ? format(new Date(pp.data_inclusao + 'T12:00:00'), 'dd/MM/yyyy') : '—'}</td>
                    <td><span className={`badge ${pp.pesquisas?.ativa ? 'badge-teal' : 'badge-gray'}`}>{pp.pesquisas?.ativa ? 'Ativa' : 'Encerrada'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showModal && (
        <ModalIncluirPesquisa
          paciente={paciente}
          pesquisasJaIncluidas={pesquisasPaciente}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

// ─── Aba: Demográfico ─────────────────────────────────────────────────────
function AbaDemografico({ paciente, comorbidades, onSaveComorbidades, isPesquisador }) {
  const { auditEditou, auditCriou } = useAudit();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(comorbidades || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(comorbidades || {}); }, [comorbidades]);

  async function handleSave() {
    setSaving(true);
    if (comorbidades?.id) {
      await supabase.from('paciente_comorbidades').update(form).eq('id', comorbidades.id);
      auditEditou('paciente_comorbidades', comorbidades.id, { paciente_id: paciente.id });
    } else {
      const { data: novo } = await supabase.from('paciente_comorbidades').insert({ ...form, paciente_id: paciente.id }).select('id').single();
      auditCriou('paciente_comorbidades', novo?.id, { paciente_id: paciente.id });
    }
    setSaving(false);
    setEditing(false);
    onSaveComorbidades();
  }

  const idade = differenceInYears(new Date(), new Date(paciente.data_nascimento));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="card">
        <div className="card-body">
          <div className="grid-2">
            <div>
              <div className="form-label">Idade</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)' }}>{idade} anos</div>
              <div className="text-sm text-muted">{format(new Date(paciente.data_nascimento + 'T12:00:00'), "d/MM/yyyy")}</div>
            </div>
            <div>
              <div className="form-label">Sexo</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{paciente.sexo === 'M' ? 'Masculino' : paciente.sexo === 'F' ? 'Feminino' : 'Outro'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Comorbidades</h2>
          {isPesquisador && !editing && <button className="btn btn-sm btn-secondary" onClick={() => setEditing(true)}>Editar</button>}
        </div>
        <div className="card-body">
          {editing ? (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 14px', border: `1px solid ${form.sem_comorbidades ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, background: form.sem_comorbidades ? '#eff6ff' : 'var(--surface)', transition: 'all .15s' }}>
                  <input type="checkbox" checked={!!form.sem_comorbidades}
                    onChange={e => {
                      if (e.target.checked) {
                        const cleared = Object.keys(COMORBIDADES_LABELS).reduce((acc, k) => ({ ...acc, [k]: false }), {});
                        setForm(f => ({ ...f, ...cleared, outras: '', sem_comorbidades: true }));
                      } else {
                        setForm(f => ({ ...f, sem_comorbidades: false }));
                      }
                    }}
                    style={{ accentColor: 'var(--blue)' }} />
                  <span style={{ fontSize: 14 }}>Sem comorbidades</span>
                </label>
                {Object.entries(COMORBIDADES_LABELS).map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: form.sem_comorbidades ? 'default' : 'pointer', padding: '8px 14px', border: `1px solid ${form[key] ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, background: form[key] ? '#eff6ff' : 'var(--surface)', opacity: form.sem_comorbidades ? .45 : 1, transition: 'all .15s' }}>
                    <input type="checkbox" checked={!!form[key]} disabled={!!form.sem_comorbidades}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.checked, ...(e.target.checked ? { sem_comorbidades: false } : {}) }))}
                      style={{ accentColor: 'var(--blue)' }} />
                    <span style={{ fontSize: 14 }}>{label}</span>
                  </label>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">Outras comorbidades</label>
                <input className="form-input" value={form.outras || ''} disabled={!!form.sem_comorbidades}
                  onChange={e => setForm(f => ({ ...f, outras: e.target.value, ...(e.target.value ? { sem_comorbidades: false } : {}) }))}
                  placeholder="Ex: DPOC, fibromialgia..." style={{ opacity: form.sem_comorbidades ? .45 : 1 }} />
              </div>
              <div className="flex-row">
                <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
              </div>
            </>
          ) : (
            <div>
              {!comorbidades ? (
                <div className="text-muted text-sm">Nenhuma comorbidade registrada.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {comorbidades.sem_comorbidades && (
                      <span className="badge badge-teal">Sem comorbidades</span>
                    )}
                    {Object.entries(COMORBIDADES_LABELS).filter(([k]) => comorbidades[k]).map(([k, label]) => (
                      <span key={k} className="badge badge-orange">{label}</span>
                    ))}
                    {!comorbidades.sem_comorbidades && !Object.keys(COMORBIDADES_LABELS).some(k => comorbidades[k]) && (
                      <span className="text-muted text-sm">Nenhuma comorbidade padronizada.</span>
                    )}
                  </div>
                  {comorbidades.outras && <div className="text-sm text-muted">Outras: {comorbidades.outras}</div>}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Aba: Cirurgia ────────────────────────────────────────────────────────
function AbaCirurgia({ pesquisasPaciente, cirurgias, onSave, isPesquisador }) {
  const { user } = useAuth();
  const { auditCriou, auditEditou } = useAudit();
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({
    pesquisa_paciente_id: pesquisasPaciente[0]?.id || '',
    data_cirurgia: '',
    tipo_cirurgia: [],
    nivel_proximal: '', nivel_distal: '',
    numero_niveis: '', sangramento_ml: '', tempo_cirurgico_min: '',
    implante: '', intercorrencias: '', observacoes: '',
    equipeCirurgica: [],
    intercorrencia_lesao_dural: false,
    intercorrencia_perda_parcial_nmio: false,
    intercorrencia_perda_total_nmio: false,
    intercorrencia_instabilidade_hemodinamica: false,
    intercorrencia_drenagem_torax: false,
    intercorrencia_lesao_vascular: false,
  });

  function set(k, v) {
    setForm(f => ({ ...f, [k]: v, ...(k === 'pesquisa_paciente_id' ? { tipo_cirurgia: [] } : {}) }));
  }

  function setEf(k, v) {
    setEditForm(f => ({ ...f, [k]: v }));
  }

  function startEdit(c) {
    setEditingId(c.id);
    setEditForm({
      data_cirurgia: c.data_cirurgia || '',
      tipo_cirurgia: c.tipo_cirurgia ? c.tipo_cirurgia.split(', ').map(t => t.trim()).filter(Boolean) : [],
      abordagem: c.abordagem || '',
      nivel_proximal: c.nivel_proximal || '',
      nivel_distal: c.nivel_distal || '',
      numero_niveis: c.numero_niveis ?? '',
      sangramento_ml: c.sangramento_ml ?? '',
      tempo_cirurgico_min: c.tempo_cirurgico_min ?? '',
      implante: c.implante || '',
      intercorrencia_lesao_dural: !!c.intercorrencia_lesao_dural,
      intercorrencia_perda_parcial_nmio: !!c.intercorrencia_perda_parcial_nmio,
      intercorrencia_perda_total_nmio: !!c.intercorrencia_perda_total_nmio,
      intercorrencia_instabilidade_hemodinamica: !!c.intercorrencia_instabilidade_hemodinamica,
      intercorrencia_drenagem_torax: !!c.intercorrencia_drenagem_torax,
      intercorrencia_lesao_vascular: !!c.intercorrencia_lesao_vascular,
      intercorrencias: c.intercorrencias || '',
      observacoes: c.observacoes || '',
      equipeCirurgica: c.equipe_cirurgica || [],
    });
  }

  async function handleUpdate(cirId) {
    if (!editForm.data_cirurgia) return;
    setSaving(true);
    const { equipeCirurgica, ...editFormRest } = editForm;
    const payload = { ...editFormRest, tipo_cirurgia: editForm.tipo_cirurgia.join(', '), equipe_cirurgica: equipeCirurgica };
    await supabase.from('cirurgias').update(payload).eq('id', cirId);
    auditEditou('cirurgias', cirId, {});
    setSaving(false);
    setEditingId(null);
    onSave();
  }

  async function handleSave() {
    if (!form.data_cirurgia) return;
    setSaving(true);
    // TODO: migrar tipo_cirurgia para text[]
    const { equipeCirurgica, ...formRest } = form;
    const formToSave = {
      ...formRest,
      tipo_cirurgia: form.tipo_cirurgia.join(', '),
      equipe_cirurgica: equipeCirurgica,
      numero_niveis: form.numero_niveis !== '' ? Number(form.numero_niveis) : null,
      sangramento_ml: form.sangramento_ml !== '' ? Number(form.sangramento_ml) : null,
      tempo_cirurgico_min: form.tempo_cirurgico_min !== '' ? Number(form.tempo_cirurgico_min) : null,
    };
    const { data: cirRes, error: cirError } = await supabase.from('cirurgias').insert({ ...formToSave, criado_por: user.id, dois_tempos: false, numero_tempo: 1 }).select().single();

    if (cirError || !cirRes) {
      console.error('Erro ao salvar cirurgia:', cirError);
      setSaving(false);
      return;
    }

    // Gerar retornos automáticos baseados nos momentos da pesquisa
    if (cirRes) {
      auditCriou('cirurgias', cirRes.id, { pesquisa_paciente_id: form.pesquisa_paciente_id, data_cirurgia: form.data_cirurgia });
      const { data: latestCir } = await supabase
        .from('cirurgias')
        .select('data_cirurgia')
        .eq('pesquisa_paciente_id', form.pesquisa_paciente_id)
        .order('data_cirurgia', { ascending: false })
        .limit(1)
        .single();
      const dataCirBase = latestCir?.data_cirurgia || form.data_cirurgia;
      const pp = pesquisasPaciente.find(p => p.id === form.pesquisa_paciente_id);
      if (pp) {
        const { data: momentos } = await supabase
          .from('pesquisa_momentos')
          .select('*')
          .eq('pesquisa_id', pp.pesquisas?.id || pp.pesquisa_id)
          .not('dias_offset', 'is', null)
          .order('ordem');

        if (momentos?.length > 0) {
          // Função: primeira quarta-feira a partir de uma data (inclusive)
          function proxQuarta(data) {
            const d = new Date(data);
            const diaSemana = d.getDay(); // 0=Dom, 3=Qua
            const diasAteQuarta = diaSemana <= 3 ? 3 - diaSemana : 10 - diaSemana;
            return addDays(d, diasAteQuarta);
          }

          const retornos = momentos.map(m => {
            const dataBase = addDays(new Date(dataCirBase + 'T12:00:00'), m.dias_offset);
            const dataQuarta = proxQuarta(dataBase);
            return {
              pesquisa_paciente_id: form.pesquisa_paciente_id,
              momento_id: m.id,
              data_prevista: format(dataQuarta, 'yyyy-MM-dd'),
              criado_por: user.id
            };
          });
          await supabase.from('agenda_retornos').insert(retornos);
        }
      }
    }

    setSaving(false);
    setShowForm(false);
    onSave();
  }

  const EQUIPE_CIRURGICA = ['Luis Eduardo Carelli', 'Alderico Girão', 'Caio Perret', 'Giancarlo Almeida', 'Gabriel Alves', 'Luis Moliterno', 'Rodrigo José', 'Felipe Carrasco', 'Antônio Eulálio', 'Marcel Nascimento', 'Guilherme Rezende', 'Fernando Bruno', 'Renato Tavares', 'Renato Teixeira', 'Gustavo Borges', 'Ricardo Meirelles', 'Paula Calassara', 'Octavio Tomé', 'Ramon Matheus', 'Heitor', 'Lucas Pupp', 'Davi Soéjima'];
  const TIPOS_FALLBACK = ['ALIF', 'OLIF', 'TLIF', 'PLIF', 'Posterolateral', 'Cervical Anterior Cage Bloqueado', 'Cervical Anterior Placa', 'Cervical Posterior', 'Escoliose', 'Descompressão Isolada'];
  const pesquisaSelecionada = pesquisasPaciente.find(p => p.id === form.pesquisa_paciente_id);
  const tiposDisponiveis = pesquisaSelecionada?.pesquisas?.tipos_cirurgia?.length > 0 ? pesquisaSelecionada.pesquisas.tipos_cirurgia : TIPOS_FALLBACK;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {cirurgias.length === 0 && !showForm && (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon">🏥</div>
            <h3>Nenhuma cirurgia registrada</h3>
            {isPesquisador && <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setShowForm(true)}>+ Registrar cirurgia</button>}
          </div>
        </div>
      )}

      {cirurgias.map((c, i) => {
        const cPp = pesquisasPaciente.find(p => p.id === c.pesquisa_paciente_id);
        const cTipos = cPp?.pesquisas?.tipos_cirurgia?.length > 0 ? cPp.pesquisas.tipos_cirurgia : TIPOS_FALLBACK;
        return (
          <div key={c.id} className="card">
            <div className="card-header">
              <h2>Cirurgia {i + 1}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="text-sm text-muted">{format(new Date(c.data_cirurgia + 'T12:00:00'), "d/MM/yyyy")}</span>
                {isPesquisador && editingId !== c.id && (
                  <button className="btn btn-sm btn-secondary" onClick={() => startEdit(c)}>Editar</button>
                )}
              </div>
            </div>
            <div className="card-body">
              {editingId === c.id ? (
                <>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Data da cirurgia *</label>
                      <input className="form-input" type="date" value={editForm.data_cirurgia} onChange={e => setEf('data_cirurgia', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Tempo cirúrgico (min)</label>
                      <input className="form-input" type="number" value={editForm.tempo_cirurgico_min} onChange={e => setEf('tempo_cirurgico_min', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Equipe Cirúrgica</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {EQUIPE_CIRURGICA.map(n => (
                        <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', border: `1px solid ${editForm.equipeCirurgica.includes(n) ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: editForm.equipeCirurgica.includes(n) ? '#eff6ff' : 'var(--surface)', fontSize: 13, transition: 'all .15s' }}>
                          <input type="checkbox" checked={editForm.equipeCirurgica.includes(n)} onChange={e => setEf('equipeCirurgica', e.target.checked ? [...editForm.equipeCirurgica, n] : editForm.equipeCirurgica.filter(x => x !== n))} style={{ accentColor: 'var(--blue)' }} />
                          {n}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo de cirurgia</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {cTipos.map(t => (
                        <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', border: `1px solid ${editForm.tipo_cirurgia.includes(t) ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: editForm.tipo_cirurgia.includes(t) ? '#eff6ff' : 'var(--surface)', fontSize: 13, transition: 'all .15s' }}>
                          <input type="checkbox" checked={editForm.tipo_cirurgia.includes(t)} onChange={e => setEf('tipo_cirurgia', e.target.checked ? [...editForm.tipo_cirurgia, t] : editForm.tipo_cirurgia.filter(x => x !== t))} style={{ accentColor: 'var(--blue)' }} />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Abordagem</label>
                      <input className="form-input" value={editForm.abordagem} onChange={e => setEf('abordagem', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Implante utilizado</label>
                      <input className="form-input" value={editForm.implante} onChange={e => setEf('implante', e.target.value)} />
                    </div>
                  </div>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Nível proximal</label>
                      <input className="form-input" value={editForm.nivel_proximal} onChange={e => setEf('nivel_proximal', e.target.value)} placeholder="Ex: T10" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nível distal</label>
                      <input className="form-input" value={editForm.nivel_distal} onChange={e => setEf('nivel_distal', e.target.value)} placeholder="Ex: S1" />
                    </div>
                  </div>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Nº de níveis</label>
                      <input className="form-input" type="number" value={editForm.numero_niveis} onChange={e => setEf('numero_niveis', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sangramento estimado (ml)</label>
                      <input className="form-input" type="number" value={editForm.sangramento_ml} onChange={e => setEf('sangramento_ml', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Intercorrências intraoperatórias</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                      {[
                        ['intercorrencia_lesao_dural', 'Lesão dural'],
                        ['intercorrencia_perda_parcial_nmio', 'Perda parcial NMIO'],
                        ['intercorrencia_perda_total_nmio', 'Perda total NMIO'],
                        ['intercorrencia_instabilidade_hemodinamica', 'Instabilidade hemodinâmica'],
                        ['intercorrencia_drenagem_torax', 'Drenagem de tórax'],
                        ['intercorrencia_lesao_vascular', 'Lesão vascular'],
                      ].map(([k, l]) => (
                        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: `1px solid ${editForm[k] ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: editForm[k] ? '#eff6ff' : 'var(--surface)', fontSize: 13 }}>
                          <input type="checkbox" checked={!!editForm[k]} onChange={e => setEf(k, e.target.checked)} style={{ accentColor: 'var(--blue)' }} />
                          {l}
                        </label>
                      ))}
                    </div>
                    <input className="form-input" value={editForm.intercorrencias || ''} onChange={e => setEf('intercorrencias', e.target.value)} placeholder="Outras intercorrências..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Observações</label>
                    <textarea className="form-textarea" value={editForm.observacoes} onChange={e => setEf('observacoes', e.target.value)} style={{ minHeight: 60 }} />
                  </div>
                  <div className="flex-row" style={{ justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={() => setEditingId(null)}>Cancelar</button>
                    <button className="btn btn-primary" onClick={() => handleUpdate(c.id)} disabled={saving || !editForm.data_cirurgia}>
                      {saving ? 'Salvando...' : 'Salvar alterações'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid-2" style={{ gap: 16 }}>
                    {[
                      ['Tipo', c.tipo_cirurgia], ['Abordagem', c.abordagem],
                      ['Nível proximal', c.nivel_proximal], ['Nível distal', c.nivel_distal],
                      ['Nº de níveis', c.numero_niveis], ['Sangramento', c.sangramento_ml ? `${c.sangramento_ml} ml` : null],
                      ['Tempo cirúrgico', c.tempo_cirurgico_min ? `${c.tempo_cirurgico_min} min` : null],
                      ['Implante', c.implante]
                    ].map(([label, val]) => val ? (
                      <div key={label}>
                        <div className="form-label">{label}</div>
                        <div style={{ fontSize: 15 }}>{val}</div>
                      </div>
                    ) : null)}
                  </div>
                  {(() => {
                    const intercLabels = [
                      ['intercorrencia_lesao_dural', 'Lesão dural'],
                      ['intercorrencia_perda_parcial_nmio', 'Perda parcial NMIO'],
                      ['intercorrencia_perda_total_nmio', 'Perda total NMIO'],
                      ['intercorrencia_instabilidade_hemodinamica', 'Instabilidade hemodinâmica'],
                      ['intercorrencia_drenagem_torax', 'Drenagem de tórax'],
                      ['intercorrencia_lesao_vascular', 'Lesão vascular'],
                    ];
                    const ativas = intercLabels.filter(([k]) => c[k]);
                    if (!ativas.length && !c.intercorrencias) return null;
                    return (
                      <div style={{ marginTop: 16 }}>
                        <div className="form-label">Intercorrências</div>
                        {ativas.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                            {ativas.map(([k, l]) => <span key={k} className="badge badge-orange">{l}</span>)}
                          </div>
                        )}
                        {c.intercorrencias && <div className="text-sm">{c.intercorrencias}</div>}
                      </div>
                    );
                  })()}
                  {c.equipe_cirurgica?.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div className="form-label">Equipe Cirúrgica</div>
                      <div style={{ fontSize: 15 }}>{c.equipe_cirurgica.join(', ')}</div>
                    </div>
                  )}
                  {c.observacoes && <div style={{ marginTop: 12 }}><div className="form-label">Observações</div><div className="text-sm">{c.observacoes}</div></div>}
                </>
              )}
            </div>
          </div>
        );
      })}

      {cirurgias.length > 0 && isPesquisador && !showForm && (
        <button className="btn btn-secondary" onClick={() => setShowForm(true)}>+ Registrar outra cirurgia</button>
      )}

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2>Nova cirurgia</h2>
            <button className="btn-close" onClick={() => setShowForm(false)}>×</button>
          </div>
          <div className="card-body">
            {pesquisasPaciente.length > 1 && (
              <div className="form-group">
                <label className="form-label">Pesquisa *</label>
                <select className="form-select" value={form.pesquisa_paciente_id} onChange={e => set('pesquisa_paciente_id', e.target.value)}>
                  {pesquisasPaciente.map(pp => <option key={pp.id} value={pp.id}>{pp.pesquisas?.titulo}</option>)}
                </select>
              </div>
            )}
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Data da cirurgia *</label>
                <input className="form-input" type="date" value={form.data_cirurgia} onChange={e => set('data_cirurgia', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Tempo cirúrgico (min)</label>
                <input className="form-input" type="number" value={form.tempo_cirurgico_min} onChange={e => set('tempo_cirurgico_min', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Equipe Cirúrgica</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {EQUIPE_CIRURGICA.map(n => (
                  <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', border: `1px solid ${form.equipeCirurgica.includes(n) ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: form.equipeCirurgica.includes(n) ? '#eff6ff' : 'var(--surface)', fontSize: 13, transition: 'all .15s' }}>
                    <input type="checkbox" checked={form.equipeCirurgica.includes(n)} onChange={e => set('equipeCirurgica', e.target.checked ? [...form.equipeCirurgica, n] : form.equipeCirurgica.filter(x => x !== n))} style={{ accentColor: 'var(--blue)' }} />
                    {n}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Tipo de cirurgia</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {tiposDisponiveis.map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', border: `1px solid ${form.tipo_cirurgia.includes(t) ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: form.tipo_cirurgia.includes(t) ? '#eff6ff' : 'var(--surface)', fontSize: 13, transition: 'all .15s' }}>
                    <input type="checkbox" checked={form.tipo_cirurgia.includes(t)} onChange={e => set('tipo_cirurgia', e.target.checked ? [...form.tipo_cirurgia, t] : form.tipo_cirurgia.filter(x => x !== t))} style={{ accentColor: 'var(--blue)' }} />
                    {t}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Nível proximal</label>
                <input className="form-input" value={form.nivel_proximal} onChange={e => set('nivel_proximal', e.target.value)} placeholder="Ex: T10" />
              </div>
              <div className="form-group">
                <label className="form-label">Nível distal</label>
                <input className="form-input" value={form.nivel_distal} onChange={e => set('nivel_distal', e.target.value)} placeholder="Ex: S1" />
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Nº de níveis</label>
                <input className="form-input" type="number" value={form.numero_niveis} onChange={e => set('numero_niveis', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Sangramento estimado (ml)</label>
                <input className="form-input" type="number" value={form.sangramento_ml} onChange={e => set('sangramento_ml', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Implante utilizado</label>
              <input className="form-input" value={form.implante} onChange={e => set('implante', e.target.value)} placeholder="Ex: parafusos pediculares + cage TLIF" />
            </div>
            <div className="form-group">
              <label className="form-label">Intercorrências intraoperatórias</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {[
                  ['intercorrencia_lesao_dural', 'Lesão dural'],
                  ['intercorrencia_perda_parcial_nmio', 'Perda parcial NMIO'],
                  ['intercorrencia_perda_total_nmio', 'Perda total NMIO'],
                  ['intercorrencia_instabilidade_hemodinamica', 'Instabilidade hemodinâmica'],
                  ['intercorrencia_drenagem_torax', 'Drenagem de tórax'],
                  ['intercorrencia_lesao_vascular', 'Lesão vascular'],
                ].map(([k, l]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: `1px solid ${form[k] ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: form[k] ? '#eff6ff' : 'var(--surface)', fontSize: 13 }}>
                    <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} style={{ accentColor: 'var(--blue)' }} />
                    {l}
                  </label>
                ))}
              </div>
              <input className="form-input" value={form.intercorrencias || ''} onChange={e => set('intercorrencias', e.target.value)} placeholder="Outras intercorrências..." />
            </div>
            <div className="form-group">
              <label className="form-label">Observações</label>
              <textarea className="form-textarea" value={form.observacoes} onChange={e => set('observacoes', e.target.value)} style={{ minHeight: 60 }} />
            </div>
            <div className="flex-row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.data_cirurgia}>
                {saving ? 'Salvando...' : 'Registrar cirurgia'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Painel de domínios SF-36 ─────────────────────────────────────────────
const DOMINIOS_ORDEM = ['CF', 'AF', 'DOR', 'EGS', 'VIT', 'AS', 'AE', 'SM'];

function corDominio(score) {
  if (score === null) return '#d1d5db';
  if (score <= 33) return '#ef4444';
  if (score <= 66) return '#f59e0b';
  return '#22c55e';
}

function PainelDominiosSF36({ dominios, isPreview }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg)', marginTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 12 }}>
        {isPreview ? 'Prévia dos Domínios SF-36 (calculado automaticamente)' : 'Domínios SF-36'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {DOMINIOS_ORDEM.map(sigla => {
          const d = dominios[sigla];
          const s = d?.score ?? null;
          const cor = corDominio(s);
          return (
            <div key={sigla} style={{ background: 'var(--surface)', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>{sigla}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: s === null ? 'var(--muted)' : cor }}>{s === null ? '—' : s}</span>
              </div>
              <div style={{ fontSize: 9, color: 'var(--muted)', marginBottom: 6, lineHeight: 1.3 }}>{d?.label}</div>
              <div style={{ height: 5, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                {s !== null && (
                  <div style={{ height: '100%', width: `${s}%`, background: cor, borderRadius: 3, transition: 'width .3s ease' }} />
                )}
              </div>
              {isPreview && d && d.camposUsados < d.camposTotais && (
                <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 3 }}>{d.camposUsados}/{d.camposTotais} campos</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Modal: Novo score ────────────────────────────────────────────────────
function ModalNovoScore({ pesquisasPaciente, momentoId, scoresDisponiveis, onClose, onSaved }) {
  const { user } = useAuth();
  const { auditCriou } = useAudit();
  const [scoreId, setScoreId] = useState('');
  const [campos, setCampos] = useState([]);
  const [respostas, setRespostas] = useState({});
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);

  async function onScoreChange(id) {
    setScoreId(id);
    setRespostas({});
    if (!id) { setCampos([]); return; }
    const { data } = await supabase.from('score_campos').select('*').eq('score_id', id).order('ordem');
    setCampos(data || []);
  }

  const scoreAtual = scoresDisponiveis.find(s => s.id === scoreId);

  function calcular() {
    if (!scoreAtual) return null;
    const nome = scoreAtual.nome;
    let soma = 0, respondidas = 0;
    campos.forEach(c => {
      const v = respostas[c.id];
      if (v !== undefined && v !== '') { soma += Number(v); respondidas++; }
    });
    if (nome.includes('ODI') || nome.includes('Oswestry') || nome.includes('NDI') || nome.includes('Neck')) {
      if (!respondidas) return null;
      return { pontuacao: Math.round((soma / (respondidas * 5)) * 100), sufixo: '%' };
    }
    if (nome.includes('mJOA') || nome.includes('HAM') || nome.includes('Hamilton')) {
      return { pontuacao: soma, sufixo: ' pts' };
    }
    if (nome.includes('SRS')) {
      return { pontuacao: respondidas ? (soma / respondidas).toFixed(2) : null, sufixo: '' };
    }
    return { pontuacao: soma, sufixo: ' pts' };
  }

  function interpretar(pontuacao) {
    if (!scoreAtual) return '';
    const nome = scoreAtual.nome;
    if (nome.includes('ODI') || nome.includes('Oswestry')) {
      if (pontuacao <= 20) return 'Incapacidade mínima'; if (pontuacao <= 40) return 'Incapacidade moderada';
      if (pontuacao <= 60) return 'Incapacidade severa'; if (pontuacao <= 80) return 'Paciente inválido';
      return 'Restrito ao leito';
    }
    if (nome.includes('NDI') || nome.includes('Neck')) {
      if (pontuacao <= 28) return 'Incapacidade leve'; if (pontuacao <= 48) return 'Incapacidade moderada';
      if (pontuacao <= 68) return 'Incapacidade severa'; return 'Incapacidade completa';
    }
    if (nome.includes('HAM') || nome.includes('Hamilton')) {
      if (pontuacao <= 7) return 'Normal'; if (pontuacao <= 13) return 'Depressão leve';
      if (pontuacao <= 18) return 'Depressão moderada'; if (pontuacao <= 22) return 'Depressão severa';
      return 'Depressão muito grave';
    }
    if (nome.includes('mJOA')) {
      if (pontuacao <= 11) return 'Mielopatia grave'; if (pontuacao <= 14) return 'Mielopatia moderada';
      return 'Mielopatia leve';
    }
    return '';
  }

  async function handleSalvar(assinar) {
    setSaving(true);
    const pp = pesquisasPaciente[0];
    const calc = calcular();
    const { data: aval } = await supabase.from('avaliacoes').insert({
      pesquisa_paciente_id: pp.id,
      momento_id: momentoId || null,
      score_id: scoreId,
      respostas,
      pontuacao_total: calc?.pontuacao ?? null,
      interpretacao: calc?.pontuacao !== null ? interpretar(calc?.pontuacao) : null,
      status: assinar ? 'assinada' : 'rascunho',
      criado_por: user.id,
      ...(assinar ? { assinado_por: user.id, assinado_em: new Date().toISOString() } : {})
    }).select('id').single();
    auditCriou('avaliacoes', aval?.id, { score_id: scoreId, status: assinar ? 'assinada' : 'rascunho', pesquisa_paciente_id: pp.id });
    setSaving(false);
    onSaved();
  }

  const calc = calcular();
  const dominiosSF36 = isSF36(scoreAtual) && step === 2 ? calcularDominiosSF36(respostas, campos) : null;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: step === 2 ? 680 : 480 }}>
        <div className="modal-header">
          <h3>{step === 1 ? 'Selecionar score' : scoreAtual?.nome}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {step === 1 && (
            <div className="form-group">
              <label className="form-label">Score clínico *</label>
              <select className="form-select" value={scoreId} onChange={e => onScoreChange(e.target.value)}>
                <option value="">Selecione...</option>
                {scoresDisponiveis.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
          )}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {campos.map(c => (
                <div key={c.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, background: 'var(--bg)' }}>
                  <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 8 }}>{c.ordem}. {c.label}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(c.opcoes || []).map(op => (
                      <label key={op.valor} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 8, border: `1px solid ${Number(respostas[c.id]) === op.valor ? 'var(--blue)' : 'var(--border)'}`, background: Number(respostas[c.id]) === op.valor ? '#eff6ff' : 'var(--surface)', cursor: 'pointer', transition: 'all .15s' }}>
                        <input type="radio" name={`c_${c.id}`} value={op.valor} checked={Number(respostas[c.id]) === op.valor} onChange={() => setRespostas(r => ({ ...r, [c.id]: op.valor }))} style={{ marginTop: 2, accentColor: 'var(--blue)' }} />
                        <span style={{ fontSize: 13 }}><strong style={{ color: 'var(--blue)', marginRight: 6 }}>{op.valor}</strong>{op.texto}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {dominiosSF36 && <PainelDominiosSF36 dominios={dominiosSF36} isPreview={true} />}
              {!dominiosSF36 && calc?.pontuacao !== null && calc?.pontuacao !== undefined && (
                <div style={{ background: 'var(--navy)', borderRadius: 12, padding: '16px 20px', color: '#fff' }}>
                  <div style={{ fontSize: 11, opacity: .6, textTransform: 'uppercase', letterSpacing: '.08em' }}>Pontuação calculada</div>
                  <div style={{ fontSize: 32, fontWeight: 700 }}>{calc.pontuacao}{calc.sufixo}</div>
                  {interpretar(calc.pontuacao) && <div style={{ fontSize: 13, opacity: .8, marginTop: 4 }}>{interpretar(calc.pontuacao)}</div>}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {step === 1 && <><button className="btn btn-secondary" onClick={onClose}>Cancelar</button><button className="btn btn-primary" onClick={() => setStep(2)} disabled={!scoreId}>Próximo →</button></>}
          {step === 2 && <><button className="btn btn-secondary" onClick={() => setStep(1)}>← Voltar</button><button className="btn btn-secondary" onClick={() => handleSalvar(false)} disabled={saving}>Salvar rascunho</button><button className="btn btn-primary" onClick={() => handleSalvar(true)} disabled={saving}>✓ Assinar</button></>}
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Nova radiografia ──────────────────────────────────────────────
function ModalNovaRadio({ pesquisasPaciente, momentoId, parametrosRadio, onClose, onSaved }) {
  const { user } = useAuth();
  const { auditCriou } = useAudit();
  const [form, setForm] = useState({ data_avaliacao: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);

  const camposAtivos = Object.entries(RADIO_LABELS).filter(([k]) => parametrosRadio?.[k]);
  const extras = parametrosRadio?.extras || [];

  async function handleSalvar() {
    setSaving(true);
    const pp = pesquisasPaciente[0];
    const extrasObj = {};
    extras.forEach(e => { if (form[`extra_${e}`]) extrasObj[e] = form[`extra_${e}`]; });
    const { data: radio } = await supabase.from('avaliacoes_radio').insert({
      pesquisa_paciente_id: pp.id,
      momento_id: momentoId || null,
      data_avaliacao: form.data_avaliacao,
      ...Object.fromEntries(camposAtivos.map(([k]) => [k, form[k] ? Number(form[k]) : null])),
      extras: extrasObj,
      status: 'rascunho',
      criado_por: user.id
    }).select('id').single();
    auditCriou('avaliacoes_radio', radio?.id, { pesquisa_paciente_id: pp.id, momento_id: momentoId });
    setSaving(false);
    onSaved();
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Parâmetros radiográficos</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="form-group">
            <label className="form-label">Data da avaliação</label>
            <input className="form-input" type="date" value={form.data_avaliacao || ''} onChange={e => setForm(f => ({ ...f, data_avaliacao: e.target.value }))} />
          </div>
          {camposAtivos.length === 0 && extras.length === 0 && (
            <div className="alert alert-info">Nenhum parâmetro radiográfico configurado para esta pesquisa. Configure em "Configurar pesquisa".</div>
          )}
          <div className="grid-2" style={{ gap: 12 }}>
            {camposAtivos.map(([k, label]) => (
              <div key={k} className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{label} (°)</label>
                <input className="form-input" type="number" step="0.1" placeholder="0.0" value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            {extras.map(e => (
              <div key={e} className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{e} (°)</label>
                <input className="form-input" type="number" step="0.1" placeholder="0.0" value={form[`extra_${e}`] || ''} onChange={ev => setForm(f => ({ ...f, [`extra_${e}`]: ev.target.value }))} />
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSalvar} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Ver avaliação ─────────────────────────────────────────────────
function ModalVerAvaliacao({ avaliacao, onClose }) {
  const [campos, setCampos] = useState([]);

  useEffect(() => {
    if (!avaliacao?.score_id) return;
    supabase.from('score_campos').select('*').eq('score_id', avaliacao.score_id).order('ordem')
      .then(({ data }) => setCampos(data || []));
  }, [avaliacao]);

  if (!avaliacao) return null;
  const respostas = avaliacao.respostas || {};
  const temRespostas = Object.keys(respostas).length > 0;
  const dominiosSF36 = isSF36({ id: avaliacao.score_id }) && campos.length > 0
    ? calcularDominiosSF36(respostas, campos)
    : null;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <h3>{avaliacao.scores?.nome}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="flex-row" style={{ marginBottom: 16 }}>
            <span className={`badge ${avaliacao.status === 'assinada' ? 'badge-teal' : 'badge-orange'}`}>
              {avaliacao.status === 'assinada' ? '✓ Assinada' : 'Rascunho'}
            </span>
            {avaliacao.assinado_em && (
              <span className="text-sm text-muted">
                {format(new Date(avaliacao.assinado_em), 'dd/MM/yyyy HH:mm')}
              </span>
            )}
          </div>

          {avaliacao.pontuacao_total !== null && (
            <div style={{ background: 'var(--navy)', borderRadius: 12, padding: '16px 20px', color: '#fff', marginBottom: 16 }}>
              <div style={{ fontSize: 11, opacity: .6, textTransform: 'uppercase', letterSpacing: '.08em' }}>Resultado</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                {avaliacao.pontuacao_total}
                {avaliacao.scores?.nome?.includes('ODI') || avaliacao.scores?.nome?.includes('NDI') ? '%' : ' pts'}
              </div>
              {avaliacao.interpretacao && <div style={{ fontSize: 13, opacity: .8, marginTop: 4 }}>{avaliacao.interpretacao}</div>}
            </div>
          )}

          {!temRespostas ? (
            <div className="alert alert-info">Avaliação sem respostas individuais registradas — apenas pontuação total armazenada.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {campos.map(c => {
                const val = respostas[c.id];
                const opcaoSel = (c.opcoes || []).find(op => Number(op.valor) === Number(val));
                return (
                  <div key={c.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, background: 'var(--bg)' }}>
                    <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 8, color: 'var(--text)' }}>{c.ordem}. {c.label}</div>
                    {opcaoSel ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: '#eff6ff', border: '1px solid var(--blue)' }}>
                        <span style={{ minWidth: 22, height: 22, borderRadius: '50%', background: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{opcaoSel.valor}</span>
                        <span style={{ fontSize: 13 }}>{opcaoSel.texto}</span>
                      </div>
                    ) : (
                      <div className="text-muted text-sm">Não respondida</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {dominiosSF36 && <PainelDominiosSF36 dominios={dominiosSF36} isPreview={false} />}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Aba: Avaliação (scores + radiografia) ────────────────────────────────
function AbaAvaliacao({ label, pesquisasPaciente, avaliacoes, avaliacoesRadio, parametrosRadio, scoresDisponiveis, isPesquisador, momentoIds, momentoIdScore, momentoIdRadio, tiposMomento, onRefresh }) {
  const { auditVisualizou } = useAudit();
  const [showScore, setShowScore] = useState(false);
  const [showRadio, setShowRadio] = useState(false);
  const [verAvaliacao, setVerAvaliacao] = useState(null);
  const temScore = tiposMomento.some(t => t === 'score' || t === 'ambos');
  const temRadio = tiposMomento.some(t => t === 'radio' || t === 'ambos');
  const avalsDoMomento = avaliacoes.filter(a => momentoIds.includes(a.momento_id));
  const radioDoMomento = avaliacoesRadio.filter(a => momentoIds.includes(a.momento_id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {temScore && (
        <div className="card">
          <div className="card-header">
            <h2>Scores clínicos — {label}</h2>
            {isPesquisador && <button className="btn btn-sm btn-primary" onClick={() => setShowScore(true)}>+ Registrar score</button>}
          </div>
          <div className="table-wrap">
            {avalsDoMomento.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">📋</div><h3>Nenhum score registrado para este momento</h3></div>
            ) : (
              <table>
                <thead><tr><th>Score</th><th>Pontuação</th><th>Interpretação</th><th>Data</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {avalsDoMomento.map(a => (
                    <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => { setVerAvaliacao(a); auditVisualizou('avaliacoes', a.id, { score_id: a.score_id }); }}>
                      <td><strong>{a.scores?.nome}</strong></td>
                      <td className="mono">{a.pontuacao_total !== null ? a.pontuacao_total : '—'}</td>
                      <td className="text-sm">{a.interpretacao || '—'}</td>
                      <td className="text-sm text-muted">{a.criado_em ? format(new Date(a.criado_em), 'dd/MM/yyyy') : '—'}</td>
                      <td><span className={`badge ${a.status === 'assinada' ? 'badge-teal' : 'badge-orange'}`}>{a.status === 'assinada' ? '✓ Assinada' : 'Rascunho'}</span></td>
                      <td><span className="btn btn-sm btn-secondary">Ver →</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {temRadio && (
        <div className="card">
          <div className="card-header">
            <h2>Parâmetros radiográficos — {label}</h2>
            {isPesquisador && <button className="btn btn-sm btn-primary" onClick={() => setShowRadio(true)}>+ Registrar radiografia</button>}
          </div>
          <div className="card-body">
            {radioDoMomento.length === 0 ? (
              <div className="empty-state" style={{ padding: 24 }}><div className="empty-icon">🩻</div><h3>Nenhuma radiografia registrada</h3></div>
            ) : radioDoMomento.map(r => (
              <div key={r.id}>
                {r.data_avaliacao && <div className="text-sm text-muted" style={{ marginBottom: 12 }}>Data: {format(new Date(r.data_avaliacao + 'T12:00:00'), 'dd/MM/yyyy')}</div>}
                <div className="grid-2" style={{ gap: 12 }}>
                  {Object.entries(RADIO_LABELS).filter(([k]) => parametrosRadio?.[k] && r[k] !== null && r[k] !== undefined).map(([k, label]) => (
                    <div key={k} style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div className="form-label" style={{ marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--navy)' }}>{r[k]}°</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showScore && (
        <ModalNovoScore
          pesquisasPaciente={pesquisasPaciente}
          momentoId={momentoIdScore}
          scoresDisponiveis={scoresDisponiveis}
          onClose={() => setShowScore(false)}
          onSaved={() => { setShowScore(false); onRefresh(); }}
        />
      )}
      {showRadio && (
        <ModalNovaRadio
          pesquisasPaciente={pesquisasPaciente}
          momentoId={momentoIdRadio}
          parametrosRadio={parametrosRadio}
          onClose={() => setShowRadio(false)}
          onSaved={() => { setShowRadio(false); onRefresh(); }}
        />
      )}
      {verAvaliacao && (
        <ModalVerAvaliacao
          avaliacao={verAvaliacao}
          onClose={() => setVerAvaliacao(null)}
        />
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────
export default function PatientViewPage() {
  const { isPesquisador } = useAuth();
  const { auditVisualizou } = useAudit();
  const location = useLocation();
  const [paciente, setPaciente] = useState(null);
  const [pesquisasPaciente, setPesquisasPaciente] = useState([]);
  const [comorbidades, setComorbidades] = useState(null);
  const [cirurgias, setCirurgias] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [avaliacoesRadio, setAvaliacoesRadio] = useState([]);
  const [avaliacoesImagem, setAvaliacoesImagem] = useState([]);
  const [momentos, setMomentos] = useState([]);
  const [scores, setScores] = useState([]);
  const [parametrosRadio, setParametrosRadio] = useState({});
  const [abaAtiva, setAbaAtiva] = useState('geral');
  const [loading, setLoading] = useState(false);
  const [preOpAssinada, setPreOpAssinada] = useState(false);
  const [agendaRetornos, setAgendaRetornos] = useState([]);

  const loadDados = useCallback(async (pac) => {
    setLoading(true);
    const [ppRes, comorbRes, preOpRes] = await Promise.all([
      supabase.from('pesquisa_pacientes').select('*, pesquisas(id, titulo, ativa, tipos_cirurgia)').eq('paciente_id', pac.id),
      supabase.from('paciente_comorbidades').select('*').eq('paciente_id', pac.id).maybeSingle(),
      supabase.from('avaliacoes_preop').select('id').eq('paciente_id', pac.id).eq('assinada', true).limit(1),
    ]);
    const pp = ppRes.data || [];
    setPesquisasPaciente(pp);
    setComorbidades(comorbRes.data);
    setPreOpAssinada((preOpRes.data || []).length > 0);

    if (pp.length > 0) {
      const ppIds = pp.map(p => p.id);
      const pesqIds = pp.map(p => p.pesquisas?.id).filter(Boolean);

      const [cirRes, avalRes, radioRes, imagemRes, momRes, paramRes, scoresRes, agendaRes] = await Promise.all([
        supabase.from('cirurgias').select('*').in('pesquisa_paciente_id', ppIds).order('data_cirurgia'),
        supabase.from('avaliacoes').select('*, scores(nome)').in('pesquisa_paciente_id', ppIds).order('criado_em'),
        supabase.from('avaliacoes_radio').select('*').in('pesquisa_paciente_id', ppIds),
        supabase.from('avaliacoes_imagem').select('*').in('pesquisa_paciente_id', ppIds),
        supabase.from('pesquisa_momentos').select('*').in('pesquisa_id', pesqIds).order('ordem'),
        supabase.from('pesquisa_parametros_radio').select('*').in('pesquisa_id', pesqIds),
        supabase.from('pesquisa_scores').select('score_id, scores(id, nome, area)').in('pesquisa_id', pesqIds),
        supabase.from('agenda_retornos').select('pesquisa_paciente_id, momento_id, data_prevista').in('pesquisa_paciente_id', ppIds),
      ]);
      setCirurgias(cirRes.data || []);
      setAvaliacoes(avalRes.data || []);
      setAvaliacoesRadio(radioRes.data || []);
      setAvaliacoesImagem(imagemRes.data || []);
      setMomentos(momRes.data || []);
      setAgendaRetornos(agendaRes.data || []);
      const paramMap = {};
      (paramRes.data || []).forEach(p => { paramMap[p.pesquisa_id] = p; });
      setParametrosRadio(paramMap);
      const scoresUnicos = [];
      const vistos = new Set();
      (scoresRes.data || []).forEach(s => {
        if (s.scores && !vistos.has(s.scores.id)) { vistos.add(s.scores.id); scoresUnicos.push(s.scores); }
      });
      setScores(scoresUnicos);
    }
    setLoading(false);
  }, []);


  function handleSelect(pac) {
    setPaciente(pac);
    setAbaAtiva('geral');
    auditVisualizou('pacientes', pac.id, { iniciais: pac.iniciais, prontuario: pac.prontuario });
    loadDados(pac);
  }

  useEffect(() => {
    if (location.state?.paciente) {
      handleSelect(location.state.paciente);
    }
  }, []); // eslint-disable-line

  // Agrupar momentos por label, agregar tipo_momento e calcular min dias_offset para ordenação
  const gruposMomentos = (() => {
    const map = new Map();
    momentos.forEach(m => {
      if (!map.has(m.label)) map.set(m.label, { label: m.label, ids: [], tipos: [], offsets: [] });
      const g = map.get(m.label);
      g.ids.push(m.id);
      g.tipos.push(m.tipo_momento);
      if (m.dias_offset !== null) g.offsets.push(m.dias_offset);
    });
    return Array.from(map.values()).map(g => ({
      label: g.label,
      ids: g.ids,
      tipos: g.tipos,
      minDiasOffset: g.offsets.length > 0 ? Math.min(...g.offsets) : null,
      momentoIdScore: momentos.find(m => g.ids.includes(m.id) && ['score', 'ambos'].includes(m.tipo_momento))?.id || null,
      momentoIdRadio: momentos.find(m => g.ids.includes(m.id) && ['radio', 'ambos'].includes(m.tipo_momento))?.id || null,
    }));
  })();

  const gruposPreOp = gruposMomentos.filter(g => g.minDiasOffset === null).sort((a, b) => a.label.localeCompare(b.label));
  const gruposPosOp = gruposMomentos.filter(g => g.minDiasOffset !== null).sort((a, b) => a.minDiasOffset - b.minDiasOffset);

  const abas = [
    { id: 'geral', label: 'Geral' },
    { id: 'demografico', label: 'Demográfico' },
    { id: 'avaliacao_preop', label: 'Anamnese' },
    ...gruposPreOp.map(g => ({ id: `m_${g.label}`, label: g.label })),
    { id: 'cirurgia', label: 'Cirurgia' },
    ...gruposPosOp.map(g => ({ id: `m_${g.label}`, label: g.label })),
  ];

  const paramUnificado = Object.values(parametrosRadio)[0] || {};

  // ─── Status dos badges ────────────────────────────────────────────────────
  const hoje = new Date();
  const scoreIdsAll = scores.map(s => s.id);
  const temParamRadioAtivo = () => Object.values(parametrosRadio).some(p =>
    Object.entries(p).some(([k, v]) => !['id','pesquisa_id','extras','criado_em','atualizado_em'].includes(k) && v === true)
  );
  const isMomCompleto = (ppId, mom) => {
    if (mom.tipo_momento === 'score' || mom.tipo_momento === 'ambos') {
      if (scoreIdsAll.length > 0 && !scoreIdsAll.every(sid =>
        avaliacoes.some(a => a.pesquisa_paciente_id === ppId && a.momento_id === mom.id && a.score_id === sid && a.status === 'assinada')
      )) return false;
    }
    if (mom.tipo_momento === 'radio' || mom.tipo_momento === 'ambos') {
      if (temParamRadioAtivo() && !avaliacoesRadio.some(r => r.pesquisa_paciente_id === ppId && r.momento_id === mom.id)) return false;
    }
    if (mom.tipo_momento === 'tc') {
      if (!avaliacoesImagem.some(img => img.pesquisa_paciente_id === ppId && img.momento_id === mom.id && img.tipo === 'tc')) return false;
    }
    if (mom.tipo_momento === 'rm') {
      if (!avaliacoesImagem.some(img => img.pesquisa_paciente_id === ppId && img.momento_id === mom.id && img.tipo === 'rm')) return false;
    }
    return true;
  };

  const statusComorb = (() => {
    if (!comorbidades) return 'pendente';
    if (comorbidades.sem_comorbidades) return 'completo';
    if (Object.keys(COMORBIDADES_LABELS).some(k => comorbidades[k]) || comorbidades.outras) return 'completo';
    return 'pendente';
  })();

  const statusPreOp = (() => {
    if (!preOpAssinada) return 'pendente';
    const momsPreOp = momentos.filter(m => m.dias_offset === null);
    for (const pp of pesquisasPaciente) {
      for (const mom of momsPreOp.filter(m => m.pesquisa_id === pp.pesquisas?.id)) {
        if (!isMomCompleto(pp.id, mom)) return 'pendente';
      }
    }
    return 'completo';
  })();

  const statusCirurgia = cirurgias.length > 0 ? 'completo' : 'pendente';

  const statusPosOp = (() => {
    if (cirurgias.length === 0) return 'pendente';
    const momsPosOp = momentos.filter(m => m.dias_offset !== null);
    if (momsPosOp.length === 0) return 'pendente';
    const posOpIds = new Set(momsPosOp.map(m => m.id));
    const temDados = avaliacoes.some(a => posOpIds.has(a.momento_id)) || avaliacoesRadio.some(r => posOpIds.has(r.momento_id)) || avaliacoesImagem.some(img => posOpIds.has(img.momento_id));
    let allDone = true, allPastDone = true, hasAnyPastMoment = false, hasFutureIncomplete = false;
    for (const pp of pesquisasPaciente) {
      for (const mom of momsPosOp.filter(m => m.pesquisa_id === pp.pesquisas?.id)) {
        const retorno = agendaRetornos.find(r => r.pesquisa_paciente_id === pp.id && r.momento_id === mom.id);
        const hasPassed = retorno?.data_prevista ? new Date(retorno.data_prevista + 'T23:59:59') < hoje : false;
        const complete = isMomCompleto(pp.id, mom);
        if (!complete) {
          allDone = false;
          if (hasPassed) { allPastDone = false; hasAnyPastMoment = true; }
          else hasFutureIncomplete = true;
        } else if (hasPassed) {
          hasAnyPastMoment = true;
        }
      }
    }
    if (allDone) return 'completo';
    if (!temDados && !hasAnyPastMoment) return 'pendente';
    if (allPastDone && hasFutureIncomplete) return 'parcial';
    return 'pendente';
  })();

  return (
    <div>
      <style>{`
        .abas-nav { display: flex; gap: 4px; overflow-x: auto; padding-bottom: 2px; margin-bottom: 20px; -webkit-overflow-scrolling: touch; }
        .aba-btn { padding: 8px 16px; border-radius: 8px; border: none; background: none; cursor: pointer; font-size: 14px; font-weight: 500; color: var(--text-2); white-space: nowrap; transition: all .15s; font-family: var(--font); }
        .aba-btn:hover { background: var(--border); color: var(--text); }
        .aba-btn.ativa { background: var(--navy); color: #fff; }
        .status-badges { display: flex; flex-wrap: wrap; gap: 8px; }
        @media (max-width: 600px) { .status-badges { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; } }
      `}</style>

      <div className="page-header">
        <div>
          <h1>Visão do Paciente</h1>
          <p>Busque um paciente para ver o histórico completo</p>
        </div>
        {paciente && <button className="btn btn-secondary" onClick={() => { setPaciente(null); setMomentos([]); }}>← Nova busca</button>}
      </div>

      {!paciente ? (
        <BuscaPaciente onSelect={handleSelect} />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 48 }} className="text-muted">Carregando dados...</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 0, padding: '14px 20px', background: 'var(--navy)', borderRadius: '12px 12px 0 0' }}>
            <div style={{ width: 44, height: 44, background: 'rgba(255,255,255,.15)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16 }}>
              {paciente.iniciais.slice(0, 2)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>{paciente.iniciais}</div>
              <div style={{ color: 'rgba(255,255,255,.6)', fontSize: 13 }}>Prontuário {paciente.prontuario} · {differenceInYears(new Date(), new Date(paciente.data_nascimento))} anos</div>
            </div>
          </div>

          <div className="status-badges" style={{ padding: '10px 16px', background: 'var(--surface)', borderRadius: '0 0 12px 12px', border: '1px solid var(--border)', borderTop: 'none', marginBottom: 20 }}>
            {[
              { key: 'comorb', status: statusComorb, ok: 'Comorbidades ✓', parcial: null, pendente: 'Comorbidades' },
              { key: 'preop',  status: statusPreOp,   ok: 'Pré-op ✓',       parcial: null, pendente: 'Pré-op' },
              { key: 'cir',    status: statusCirurgia, ok: 'Cirurgia ✓',     parcial: null, pendente: 'Cirurgia' },
              { key: 'posop',  status: statusPosOp,   ok: 'Pós-op ✓',       parcial: 'Pós-op em dia', pendente: 'Pós-op' },
            ].map(({ key, status, ok, parcial, pendente }) => {
              const cfg = status === 'completo'
                ? { bg: '#d1fae5', color: '#065f46', text: ok }
                : status === 'parcial'
                ? { bg: '#dbeafe', color: '#1e40af', text: parcial }
                : { bg: '#fef3c7', color: '#92400e', text: pendente };
              return (
                <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 20, background: cfg.bg, color: cfg.color, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                  {cfg.text}
                </span>
              );
            })}
          </div>

          <div className="abas-nav">
            {abas.map(a => (
              <button key={a.id} className={`aba-btn ${abaAtiva === a.id ? 'ativa' : ''}`} onClick={() => setAbaAtiva(a.id)}>
                {a.label}
              </button>
            ))}
          </div>

          {abaAtiva === 'geral' && <AbaGeral paciente={paciente} pesquisasPaciente={pesquisasPaciente} isPesquisador={isPesquisador} onRefresh={() => loadDados(paciente)} />}
          {abaAtiva === 'demografico' && <AbaDemografico paciente={paciente} comorbidades={comorbidades} onSaveComorbidades={() => loadDados(paciente)} isPesquisador={isPesquisador} />}
          {abaAtiva === 'avaliacao_preop' && (
            <AvaliacaoPreOpTab pacienteId={paciente.id} canEdit={isPesquisador} />
          )}
          {abaAtiva === 'cirurgia' && (
            <AbaCirurgia
              pesquisasPaciente={pesquisasPaciente}
              cirurgias={cirurgias}
              onSave={() => loadDados(paciente)}
              isPesquisador={isPesquisador}
            />
          )}
          {[...gruposPreOp, ...gruposPosOp].map(g => abaAtiva === `m_${g.label}` && (
            <AbaAvaliacao
              key={g.label}
              label={g.label}
              pesquisasPaciente={pesquisasPaciente}
              avaliacoes={avaliacoes}
              avaliacoesRadio={avaliacoesRadio}
              parametrosRadio={paramUnificado}
              scoresDisponiveis={scores}
              isPesquisador={isPesquisador}
              momentoIds={g.ids}
              momentoIdScore={g.momentoIdScore}
              momentoIdRadio={g.momentoIdRadio}
              tiposMomento={g.tipos}
              onRefresh={() => loadDados(paciente)}
            />
          ))}
        </>
      )}
    </div>
  );
}
